import { setState, getState, type GameState } from '../store';
import { resolveCode, joinByCode, type ResolveCodeResponse, orgLogin, getCapabilities } from '../api/game-api';
import { connectTeamWs } from '../api/team-sync';
import { navigate } from '../router';
import { getInstallPrompt, clearInstallPrompt } from '../main';
import { escapeHtml } from '../utils/escape';
import { createThemeToggle } from '../components/theme-toggle';
import { requestNotificationPermission } from '../utils/notifications';
import { anchorFromServer } from '../utils/time-utils';
import { armAuthFailureHandler } from '../utils/auth-failure';
import { prefetchAddressDb } from '../data/address-service';

/**
 * join-view — D·Role Declaration layout.
 *
 * State machine:
 *   (empty)            → big AUTOQUEST hero, code input, disabled CTA
 *   resolved.captain   → tiny AUTOQUEST tag, КАПИТАН (amber) + TEAMNAME hero, amber code border, "ВОЙТИ КАК КАПИТАН" CTA
 *   resolved.regular   → tiny tag, КОМАНДА (gray) + TEAMNAME hero, neutral code border, "ПРИСОЕДИНИТЬСЯ К КОМАНДЕ" CTA
 *   resolved.spectator → tiny tag, НАБЛЮДАТЕЛЬ (info blue) + GAME TITLE hero + one-line description, info-blue code border, "СЛЕДИТЬ ЗА КВЕСТОМ" CTA, no nickname field
 *   resolved.legacy    → tiny tag, КОМАНДА (gray) + GAME TITLE hero (no team exists), neutral code border, "ПРИСОЕДИНИТЬСЯ" CTA
 *
 * The body class is toggled between `join-view--empty` / `join-view--captain` /
 * `join-view--regular` / `join-view--spectator` so CSS handles the per-role
 * color accents on one DOM structure. Team names are clamped via
 * `font-size: clamp(...)` so 100-char names don't overflow.
 */

type RoleVariant = 'empty' | 'captain' | 'regular' | 'spectator';

/**
 * Session 52 post-ship — fields that MUST be explicitly zeroed on every
 * fresh join. Before this fix, `completedAt` (and siblings) carried over
 * from a previously-finished game via persisted localStorage, and `main.ts`
 * unconditionally routed users with `completedAt > 0` to the results
 * screen on every page load — so F5 in the middle of a new game dumped
 * the player onto a stale "Квест пройден" page. Reset these explicitly in
 * BOTH the demo-auto-join path and the regular-resolve/join path.
 *
 * Typed as `Partial<GameState>` (not `as const`) so TypeScript accepts the
 * spread into `setState`'s `Partial<GameState>` param without widening
 * readonly-literal arrays.
 */
const JOIN_RESET_FIELDS: Partial<GameState> = {
  completedAt: 0,
  totalTimeMs: 0,
  penaltyMs: 0,
  wrongAnswers: 0,
  geoVerified: undefined,
  hintNavigateData: undefined,
  chatMessages: [],
  unreadChatCount: 0,
  leaderboard: [],
  currentRiddleText: '',
  currentHintText: '',
};

/**
 * Session 42 intentional dual-entry architecture.
 *
 * The organizer login form in this view is duplicated with the server-
 * rendered page at `/dashboard/login` (DashboardAuthPages.kt). Reason:
 * server-initiated flows (OAuth callbacks, password-reset, session-
 * expire, logout) cannot 302 into a hash-route like `/app/#/join`, so
 * `/dashboard/login` is the landing page for all those redirects. This
 * PWA copy exists so the primary "open autokvest.ru, log in" UX does
 * not force a full-page transition.
 *
 * Cross-links between the two pages ("Я игрок →" on dashboard, "Войти
 * как организатор →" toggle here) are intentional safety nets.
 *
 * If you change fields, OAuth buttons, reset links, or validation copy,
 * update BOTH files or the two entry points will drift.
 */
export function renderJoinView(container: HTMLElement): void {
  // Demo mode auto-join: dashboard stores full JoinGameResponse in localStorage
  const demoData = localStorage.getItem('aq_demo_join');
  if (demoData) {
    localStorage.removeItem('aq_demo_join');
    try {
      const res = JSON.parse(demoData);
      setState({
        ...JOIN_RESET_FIELDS,
        gameCode: res.gameCode || '',
        gameTitle: res.gameTitle || '',
        playerId: res.playerId,
        playerToken: res.playerToken || '',
        playerName: res.teamName || 'Демо',
        playerRole: 'PLAYER',
        isCaptain: res.isCaptain ?? true,
        teamName: res.teamName || '',
        organizerPhone: res.organizerPhone || '',
        fullOffline: res.fullOffline || false,
        allowManualSkip: res.allowManualSkip || false,
        status: res.gameStarted ? 'ACTIVE' : 'WAITING',
        salt: res.salt || '',
        firstRiddleKey: res.firstRiddleKey,
        locations: res.locations.map((l: any) => ({
          id: l.id,
          order: l.orderIndex,
          encryptedRiddle: l.encryptedRiddleText,
          encryptedHint: l.encryptedRiddleHint,
          hintDelaySec: l.hintDelayMin * 60,
          timeLimitSec: l.timeLimitMin * 60,
          answerType: l.answerType,
          geofenceRadiusM: l.geofenceRadiusM,
          siteCodeHash: l.siteCodeHash,
          encryptedAnswerData: l.encryptedAnswerData || undefined,
          answerHash: l.answerHash || undefined,
          siteCodeDigitsOnly: l.siteCodeDigitsOnly,
          siteCodeRequired: l.siteCodeRequired,
          siteCodeCount: l.siteCodeCount,
          siteCodeRequiredFlags: l.siteCodeRequiredFlags,
          mediaUrl: l.mediaUrl,
          mediaType: l.mediaType,
          additionalMedia: l.additionalMedia || [],
          encryptedHints: l.encryptedHints || [],
        })),
        allowTeamChat: res.allowTeamChat ?? false,
        canChat: res.canChat ?? false,
        lobbyChatMode: res.lobbyChatMode || 'OFF',
        gameRules: res.gameRules ?? null,
        demoSpeedMultiplier: res.demoSpeedMultiplier || 1,
        demoScript: Array.isArray(res.demoScript) ? res.demoScript : [],
        currentLocationIndex: res.resumeLocationIndex || 0,
        nextKeys: (() => {
          // When resuming at location N > 0, seed the key chain so
          // location-view can decrypt the riddle at the resume index.
          const idx = res.resumeLocationIndex || 0;
          if (idx > 0 && res.resumeKey) {
            const keys: string[] = [];
            keys[idx - 1] = res.resumeKey;
            return keys;
          }
          return [];
        })(),
        error: null,
      });

      // Session 50 — demo-join path also opens the team WS.
      connectTeamWs(res.gameCode || '', res.playerId, res.playerToken || '');

      if (res.gameStarted) {
        // Session 50 post-ship fix #2: clock-skew-corrected local anchor.
        // `anchorFromServer` translates the server-observed origin into
        // THIS device's local clock frame so `Date.now() - anchor` returns
        // the team's true elapsed regardless of how much this phone's
        // clock disagrees with the server. Falls back to Date.now() only
        // when the server couldn't anchor (null locationEnteredAt).
        const anchor = anchorFromServer(res.locationEnteredAt, res.serverNow);
        setState({
          locationStartedAt: anchor,
          gameStartedAt: anchor,
        });
        navigate('riddle');
      } else {
        navigate('lobby');
      }
      return;
    } catch (e) {
      console.error('Demo join parse failed:', e);
    }
  }

  container.innerHTML = `
    <div id="join-root" class="screen screen-enter screen-split join-view join-view--empty">
      <div id="join-theme-toggle" style="position: absolute; top: var(--sp-xl); right: var(--sp-md); z-index: 10;"></div>

      <!-- HERO column: left in landscape, top in portrait. Both empty
           and resolved variants live here; JS swaps display: none. -->
      <div class="screen-content">
        <!-- STATE: empty — shown before the code resolves.
             Tagline swaps to «Панель организатора» when org mode is active
             (single source of truth for the brand wordmark — the inner
             duplicate hero in #join-org-section was removed 2026-04-27 to
             fix portrait-mobile «AUTOKVEST» × 2 stacking). -->
        <div id="join-empty-hero" class="join-empty-hero">
          <h1 class="text-display-large" style="color: var(--amber); margin-bottom: var(--sp-xs);">AUTOKVEST</h1>
          <p id="join-hero-tagline" class="text-body" style="color: var(--text-secondary);">Ночные автоквесты</p>
        </div>

        <!-- STATE: resolved — shown after the code resolves -->
        <div id="join-resolved-hero" class="join-resolved-hero" style="display: none;">
          <div class="text-eyebrow join-tiny-tag">AUTOKVEST</div>
          <div class="join-role-hero">
            <div id="join-role-label" class="text-eyebrow join-role-label">КОМАНДА</div>
            <div id="join-hero-text" class="join-hero-text">TEAMRED</div>
            <div id="join-hero-subtitle" class="join-hero-subtitle">Game title</div>
            <p id="join-spectator-desc" class="join-spectator-desc" style="display: none;">
              Ты не играешь — ты смотришь. Видишь команды, точки, лидерборд в реальном времени.
            </p>
          </div>
          <div class="join-divider"></div>
        </div>
      </div>

      <!-- ACTION column: right in landscape, below hero in portrait.
           Holds player form, organizer login, and the toggle link. -->
      <div class="screen-controls">

        <!-- PLAYER SECTION (default visible) -->
        <div id="join-player-section">
          <!-- Form (code input styling changes per role via body class) -->
          <div class="join-form">
            <div>
              <label id="join-code-label" class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">КОД</label>
              <input id="join-code" class="input join-code-input" type="text" maxlength="8"
                     placeholder="Код команды или наблюдателя" autocomplete="off" />
            </div>

            <div id="join-name-row">
              <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">
                НИКНЕЙМ<span id="join-name-required" style="color: var(--amber); display: none;"> *</span>
              </label>
              <input id="join-name" class="input" type="text" maxlength="20"
                     placeholder="Как вас зовут?" autocomplete="off" />
              <p id="join-name-hint" class="text-body" style="display: none; color: var(--text-hint); font-size: 12px; margin-top: 4px;">
                Чат открыт для всех игроков — введите имя, чтобы команда понимала, кто пишет.
              </p>
            </div>

            <div id="join-error" class="text-body" style="color: var(--danger); display: none; text-align: center;"></div>

            <button id="join-btn" class="btn btn-primary btn-full" disabled>
              ВОЙТИ В ИГРУ
            </button>

            <button id="pwa-install-btn" class="btn btn-secondary btn-full" style="display: none;">
              Установить приложение
            </button>
          </div>
        </div>

        <!-- ORGANIZER SECTION — see renderJoinView JSDoc above for the
             session-42 dual-entry rationale. TL;DR: this form is
             duplicated with DashboardAuthPages.kt on purpose (server
             flows can't 302 into a hash-route). Update both together. -->
        <div id="join-org-section" class="join-org-block" style="display: none;">
          <!-- Inner hero deleted 2026-04-27 — outer #join-empty-hero is the
               single source of truth for the AUTOKVEST wordmark; its tagline
               swaps via #join-hero-tagline on mode toggle. Pre-fix portrait
               mobile stacked the two heroes vertically → «AUTOKVEST» × 2. -->
          <div class="join-org-card">
            <div id="join-org-error" style="display: none; color: var(--danger); text-align: center; font-size: 14px; margin-bottom: var(--sp-sm);"></div>

            <div style="margin-bottom: var(--sp-gap);">
              <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">EMAIL</label>
              <input id="join-org-email" class="input" type="email" placeholder="email@example.com" autocomplete="email" />
            </div>
            <div style="margin-bottom: var(--sp-md);">
              <label class="text-eyebrow" style="display: block; margin-bottom: var(--sp-xs);">ПАРОЛЬ</label>
              <div class="password-field">
                <input id="join-org-password" class="input" type="password" placeholder="Пароль" autocomplete="current-password" />
                <button type="button" id="join-org-password-toggle" class="password-field-toggle" aria-controls="join-org-password" aria-pressed="false" aria-label="Показать пароль">
                  <svg class="icon-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg class="icon-eye-off" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>

            <button id="join-org-btn" class="btn btn-primary btn-full">Войти</button>

            <div id="join-org-oauth" style="display: none;">
              <div style="text-align: center; color: var(--text-secondary); font-size: 14px; margin: var(--sp-md) 0;">или</div>
              <div style="display: flex; flex-direction: column; gap: var(--sp-sm);">
                <a id="join-org-yandex" href="/auth/yandex" class="btn btn-full" style="display: none; background: var(--brand-yandex, #FC3F1D); color: white; text-align: center; text-decoration: none; justify-content: center;">Войти через Яндекс</a>
                <a id="join-org-google" href="/auth/google" class="btn btn-outline btn-full" style="display: none; text-align: center; text-decoration: none; justify-content: center;">Войти через Google</a>
              </div>
            </div>

            <div style="text-align: center; margin-top: var(--sp-md); font-size: 14px;">
              <a href="/dashboard/forgot-password" style="color: var(--text-secondary); text-decoration: none;">Забыли пароль?</a>
              <span style="color: var(--text-hint); margin: 0 var(--sp-sm);">&middot;</span>
              <a href="/dashboard/register" style="color: var(--text-secondary); text-decoration: none;">Зарегистрироваться</a>
            </div>
          </div>
        </div>

        <!-- Toggle link (always visible, switches between player/org).
             Copy: "Войти как организатор →" is deliberately an action-
             verb, not a destination. The old "Организатор? Панель
             управления →" read like a page-navigation promise while the
             behaviour is an in-place DOM swap. -->
        <div id="join-mode-toggle" class="join-org-link" style="cursor: pointer;">
          Войти как организатор &rarr;
        </div>
      </div>
    </div>
  `;

  const root = container.querySelector<HTMLElement>('#join-root')!;
  const emptyHero = container.querySelector<HTMLElement>('#join-empty-hero')!;
  const resolvedHero = container.querySelector<HTMLElement>('#join-resolved-hero')!;
  const roleLabel = container.querySelector<HTMLElement>('#join-role-label')!;
  const heroText = container.querySelector<HTMLElement>('#join-hero-text')!;
  const heroSubtitle = container.querySelector<HTMLElement>('#join-hero-subtitle')!;
  const spectatorDesc = container.querySelector<HTMLElement>('#join-spectator-desc')!;
  const codeLabel = container.querySelector<HTMLElement>('#join-code-label')!;
  const codeInput = container.querySelector<HTMLInputElement>('#join-code')!;
  const nameRow = container.querySelector<HTMLElement>('#join-name-row')!;
  const nameInput = container.querySelector<HTMLInputElement>('#join-name')!;
  const errorEl = container.querySelector<HTMLElement>('#join-error')!;
  const btn = container.querySelector<HTMLButtonElement>('#join-btn')!;

  // Theme toggle in top-right corner
  createThemeToggle(container.querySelector<HTMLElement>('#join-theme-toggle')!);

  let resolvedCode: ResolveCodeResponse | null = null;
  let resolveTimer: ReturnType<typeof setTimeout> | null = null;

  function setVariant(variant: RoleVariant): void {
    root.classList.remove(
      'join-view--empty',
      'join-view--captain',
      'join-view--regular',
      'join-view--spectator'
    );
    root.classList.add(`join-view--${variant}`);
  }

  function validate(): void {
    const code = codeInput.value.trim();
    if (!resolvedCode || code.length < 8) {
      btn.disabled = true;
      return;
    }

    if (resolvedCode.type === 'spectator') {
      // Spectators don't need a nickname
      btn.disabled = false;
    } else if (resolvedCode.type === 'team') {
      // Team players share a team name by default — no nickname needed.
      // BUT: if the organizer opened chat to non-captains, every player must
      // supply a nickname (otherwise chat messages have no sender).
      if (resolvedCode.allowTeamChat) {
        btn.disabled = nameInput.value.trim().length === 0;
      } else {
        btn.disabled = false;
      }
    } else {
      // Legacy: need nickname
      btn.disabled = nameInput.value.trim().length === 0;
    }
  }

  function updateUIForCodeType(res: ResolveCodeResponse): void {
    resolvedCode = res;
    errorEl.style.display = 'none';

    // Swap the hero section: empty → resolved with a gentle fade
    emptyHero.style.display = 'none';
    resolvedHero.style.display = '';

    const nameRequiredMark = container.querySelector<HTMLElement>('#join-name-required')!;
    const nameHint = container.querySelector<HTMLElement>('#join-name-hint')!;

    if (res.type === 'team') {
      const variant: RoleVariant = res.isCaptainCode ? 'captain' : 'regular';
      setVariant(variant);

      roleLabel.textContent = res.isCaptainCode ? 'КАПИТАН' : 'КОМАНДА';
      heroText.textContent = res.teamName ?? '';
      heroSubtitle.textContent = res.gameTitle ?? '';
      // Use innerHTML ONLY for escaped content — the hero fields set textContent
      // above so no XSS surface here. The spectator description stays static.
      spectatorDesc.style.display = 'none';

      nameRow.style.display = '';
      codeLabel.textContent = res.isCaptainCode ? 'КОД КАПИТАНА' : 'КОД КОМАНДЫ';

      // Nickname is required if the team chat is open to regular players.
      nameRequiredMark.style.display = res.allowTeamChat ? '' : 'none';
      nameHint.style.display = res.allowTeamChat ? 'block' : 'none';

      btn.textContent = res.isCaptainCode ? 'ВОЙТИ КАК КАПИТАН' : 'ПРИСОЕДИНИТЬСЯ К КОМАНДЕ';
    } else if (res.type === 'spectator') {
      setVariant('spectator');

      roleLabel.textContent = 'НАБЛЮДАТЕЛЬ';
      heroText.textContent = res.gameTitle ?? '';
      heroSubtitle.textContent = '';
      spectatorDesc.style.display = '';

      nameRow.style.display = 'none';
      codeLabel.textContent = 'КОД НАБЛЮДАТЕЛЯ';
      nameRequiredMark.style.display = 'none';
      nameHint.style.display = 'none';

      btn.textContent = 'СЛЕДИТЬ ЗА КВЕСТОМ';
    } else {
      // Legacy code path (no teams, no spectator). Falls back to the regular
      // player flavor with the game title as the hero.
      setVariant('regular');

      roleLabel.textContent = 'ИГРА';
      heroText.textContent = res.gameTitle ?? '';
      heroSubtitle.textContent = '';
      spectatorDesc.style.display = 'none';

      nameRow.style.display = '';
      codeLabel.textContent = 'КОД';
      nameRequiredMark.style.display = '';
      nameHint.style.display = 'none';

      btn.textContent = 'ВОЙТИ В ИГРУ';
    }

    // Sanitize the hero text before rendering to innerHTML in case any field
    // ever moves back to innerHTML assignment. textContent already escapes,
    // but we add a belt here in case of a future refactor.
    void escapeHtml; // keep import used; static text above is already safe

    validate();
  }

  function resetResolve(): void {
    resolvedCode = null;
    setVariant('empty');
    emptyHero.style.display = '';
    resolvedHero.style.display = 'none';
    nameRow.style.display = '';
    codeLabel.textContent = 'КОД';
    btn.disabled = true;
    btn.textContent = 'ВОЙТИ В ИГРУ';
  }

  // Debounced code resolution
  codeInput.addEventListener('input', () => {
    // Allow alphanumeric
    codeInput.value = codeInput.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase();

    if (resolveTimer) clearTimeout(resolveTimer);
    resetResolve();

    const code = codeInput.value.trim();
    if (code.length < 8) return;

    resolveTimer = setTimeout(async () => {
      try {
        const res = await resolveCode(code);
        updateUIForCodeType(res);
      } catch {
        resetResolve();
        errorEl.textContent = 'Код не найден';
        errorEl.style.display = 'block';
      }
    }, 400);
  });

  // PWA install button
  const installBtn = container.querySelector<HTMLButtonElement>('#pwa-install-btn')!;
  if (getInstallPrompt()) {
    installBtn.style.display = '';
  }
  installBtn.addEventListener('click', async () => {
    const prompt = getInstallPrompt();
    if (!prompt || !prompt.prompt) return;
    prompt.prompt();
    const result = await prompt.userChoice;
    if (result?.outcome === 'accepted') {
      clearInstallPrompt();
      installBtn.style.display = 'none';
    }
  });

  nameInput.addEventListener('input', validate);

  // ── Player ↔ Organizer view toggle ─────────────────────────────────
  const playerSection = container.querySelector<HTMLElement>('#join-player-section')!;
  const orgSection = container.querySelector<HTMLElement>('#join-org-section')!;
  const modeToggle = container.querySelector<HTMLElement>('#join-mode-toggle')!;
  const orgEmail = container.querySelector<HTMLInputElement>('#join-org-email')!;
  const orgPassword = container.querySelector<HTMLInputElement>('#join-org-password')!;
  const orgPasswordToggle = container.querySelector<HTMLButtonElement>('#join-org-password-toggle')!;
  const orgBtn = container.querySelector<HTMLButtonElement>('#join-org-btn')!;
  const orgError = container.querySelector<HTMLElement>('#join-org-error')!;
  let orgCapsFetched = false;
  let showingOrg = false;

  // Session 66 — password reveal toggle. Matches the dashboard pattern
  // (static/js/password-toggle.js) but wired directly here since the
  // PWA bundle doesn't pull in delegated-listener scripts.
  orgPasswordToggle.addEventListener('click', () => {
    const reveal = orgPassword.type === 'password';
    orgPassword.type = reveal ? 'text' : 'password';
    orgPasswordToggle.setAttribute('aria-pressed', reveal ? 'true' : 'false');
    orgPasswordToggle.setAttribute('aria-label', reveal ? 'Скрыть пароль' : 'Показать пароль');
  });

  // /investigate 2026-04-27 user report: «Захожу на андроиде или айфоне
  // в Войти как организатор — мне показывает два заголовка: Autokvest
  // Ночные автоквесты, и под ним ещё один — Autokvest Панель организатора».
  // Outer + inner hero stacking on portrait mobile (landscape split-pane
  // shipped DISABLED at @media trigger per session 78). Fix: removed the
  // inner duplicate; outer hero's tagline now swaps based on mode AND the
  // resolved-hero is force-hidden on org-mode entry (defensive — a player
  // who resolved a code, then toggled to org mode, would have seen the
  // team-info hero behind the org form, which is meaningless).
  const heroTagline = container.querySelector<HTMLElement>('#join-hero-tagline')!;

  modeToggle.addEventListener('click', () => {
    showingOrg = !showingOrg;
    playerSection.style.display = showingOrg ? 'none' : '';
    orgSection.style.display = showingOrg ? '' : 'none';
    modeToggle.innerHTML = showingOrg
      ? '&larr; Войти как игрок'
      : 'Войти как организатор &rarr;';
    heroTagline.textContent = showingOrg ? 'Панель организатора' : 'Ночные автоквесты';
    if (showingOrg) {
      // Force the empty-hero variant — resolved-team hero is meaningless
      // in org-login flow.
      emptyHero.style.display = '';
      resolvedHero.style.display = 'none';
    } else if (resolvedCode) {
      // Restore resolved-hero on the way BACK to player mode if a code
      // had been resolved before the toggle.
      emptyHero.style.display = 'none';
      resolvedHero.style.display = '';
    }
    if (showingOrg && !orgCapsFetched) {
      orgCapsFetched = true;
      getCapabilities().then(caps => {
        const oauthSection = container.querySelector<HTMLElement>('#join-org-oauth')!;
        const yandexBtn = container.querySelector<HTMLElement>('#join-org-yandex')!;
        const googleBtn = container.querySelector<HTMLElement>('#join-org-google')!;
        if (caps.hasYandexOAuth || caps.hasGoogleOAuth) {
          oauthSection.style.display = '';
          if (caps.hasYandexOAuth) yandexBtn.style.display = '';
          if (caps.hasGoogleOAuth) googleBtn.style.display = '';
        }
      }).catch(() => {});
    }
  });

  const ERROR_MESSAGES: Record<string, string> = {
    email_not_found: 'Email не зарегистрирован. Для создания аккаунта нужна ссылка-приглашение.',
    invalid_credentials: 'Неверный пароль',
    rate_limited: 'Слишком много попыток. Подождите 15 минут.',
  };

  async function handleOrgLogin() {
    const email = orgEmail.value.trim();
    const password = orgPassword.value;
    if (!email || !password) {
      orgError.textContent = 'Заполните email и пароль';
      orgError.style.display = '';
      return;
    }
    orgBtn.disabled = true;
    orgBtn.textContent = 'Вход...';
    orgError.style.display = 'none';
    try {
      await orgLogin(email, password);
      window.location.href = '/dashboard';
    } catch (err) {
      const key = err instanceof Error ? err.message : 'unknown';
      orgError.textContent = ERROR_MESSAGES[key] || 'Ошибка входа';
      orgError.style.display = '';
      orgBtn.disabled = false;
      orgBtn.textContent = 'Войти';
    }
  }

  orgBtn.addEventListener('click', handleOrgLogin);
  orgPassword.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleOrgLogin();
  });

  btn.addEventListener('click', async () => {
    if (!resolvedCode) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Подключение...';
    errorEl.style.display = 'none';

    const code = codeInput.value.trim();
    const nickname = nameInput.value.trim();
    // Captain status is now determined by which code the player used
    // (regular vs captain). We still pass false in the request for legacy
    // compat; the backend will flip it to true if the code was a captain
    // code, and the authoritative value comes back in res.isCaptain.
    const isCaptainGuess = !!resolvedCode.isCaptainCode;

    try {
      // Session 50 /review merged_bug_005 — forward the stored playerToken
      // if we have one so the server recognizes a rejoin and reuses the
      // existing Players row. First-time joiners have no token yet and
      // hit the fresh-insert path + partial unique index.
      const existingToken = getState().playerToken;
      const res = await joinByCode(code, nickname, false, existingToken || undefined);

      // v0.79.30 (Bug 4) — fresh session starts here. Re-arm the auth-failure
      // handler so the next 401/403/WS-1008 (in this NEW session) can fire
      // the logout flow. clearAllGameData no longer does this — see comment
      // on `resetAuthFailureFlag` in auth-failure.ts for why moving the
      // arming out of the handler-invoked clear is necessary.
      armAuthFailureHandler();

      const role = res.role || (resolvedCode.type === 'spectator' ? 'SPECTATOR' : 'PLAYER');
      const teamName = res.teamName || resolvedCode.teamName || '';
      const isCaptain = res.isCaptain ?? isCaptainGuess;

      setState({
        ...JOIN_RESET_FIELDS,
        gameCode: res.gameCode || code,
        gameTitle: res.gameTitle || '',
        playerId: res.playerId,
        playerToken: res.playerToken || '',
        // Session 67 /qa — prefer the server's final nickname so a blank-nick
        // captain sees «Капитан #ABCDEF» in their own chat header / results
        // screen, matching what other players see in team chat. Falls back to
        // teamName for regular players (server returns teamName there too,
        // so no behavior change) and finally to the user-typed nickname.
        playerName: ((res as { nickname?: string }).nickname || '').trim()
          || teamName
          || nickname
          || 'Наблюдатель',
        playerRole: role as 'PLAYER' | 'SPECTATOR',
        isCaptain,
        teamName,
        organizerPhone: res.organizerPhone || '',
        fullOffline: res.fullOffline || false,
        allowManualSkip: res.allowManualSkip || false,
        status: res.gameStarted ? 'ACTIVE' : 'WAITING',
        salt: res.salt || '',
        firstRiddleKey: res.firstRiddleKey,
        locations: res.locations.map(l => ({
          id: l.id,
          order: l.orderIndex,
          encryptedRiddle: l.encryptedRiddleText,
          encryptedHint: l.encryptedRiddleHint,
          hintDelaySec: l.hintDelayMin * 60,
          timeLimitSec: l.timeLimitMin * 60,
          answerType: l.answerType,
          geofenceRadiusM: l.geofenceRadiusM,
          siteCodeHash: l.siteCodeHash,
          encryptedAnswerData: l.encryptedAnswerData || undefined,
          answerHash: l.answerHash || undefined,
          siteCodeDigitsOnly: l.siteCodeDigitsOnly,
          siteCodeRequired: l.siteCodeRequired,
          siteCodeCount: l.siteCodeCount,
          siteCodeRequiredFlags: l.siteCodeRequiredFlags,
          mediaUrl: l.mediaUrl,
          mediaType: l.mediaType,
          additionalMedia: l.additionalMedia || [],
          encryptedHints: l.encryptedHints || [],
        })),
        allowTeamChat: res.allowTeamChat ?? false,
        canChat: res.canChat ?? false,
        lobbyChatMode: res.lobbyChatMode || 'OFF',
        gameRules: res.gameRules ?? null,
        demoSpeedMultiplier: res.demoSpeedMultiplier || 1,
        demoScript: Array.isArray(res.demoScript) ? res.demoScript : [],
        currentLocationIndex: res.resumeLocationIndex || 0,
        nextKeys: (() => {
          const idx = res.resumeLocationIndex || 0;
          if (idx > 0 && res.resumeKey) {
            const keys: string[] = [];
            keys[idx - 1] = res.resumeKey;
            return keys;
          }
          return [];
        })(),
        error: null,
      });

      // Request notification permission after successful join
      requestNotificationPermission().catch(() => {});

      // Session 50 §5d.bis — open the team WS so ADVANCE / CAUGHT_UP /
      // FINISH events from teammates arrive in real time. Spectators +
      // legacy solo (no teamId) are silently skipped inside connectTeamWs.
      connectTeamWs(res.gameCode || code, res.playerId, res.playerToken || '');

      // v0.79.41 — fire-and-forget FIAS prefetch for fresh joins. Spectators
      // never enter addresses, so skip. Idempotent — duplicate calls in the
      // same session are no-ops via the inflight guard in address-service.
      // The 10.4 MB blob lands in Workbox `fias-db` cache (CacheFirst, 30d)
      // before the player reaches geo-input mid-quest.
      if (role === 'PLAYER') {
        prefetchAddressDb();
      }

      if (role === 'SPECTATOR') {
        navigate('spectator');
      } else if (res.gameStarted) {
        // Session 50 post-ship fix #2 — see anchorFromServer/computeLocalAnchor
        // for why we translate server time into the device's clock frame
        // instead of using the raw server timestamp.
        const anchor = anchorFromServer(res.locationEnteredAt, res.serverNow);
        setState({
          locationStartedAt: anchor,
          gameStartedAt: anchor,
        });
        navigate('riddle');
      } else {
        navigate('lobby');
      }
    } catch (err) {
      errorEl.textContent = err instanceof Error ? err.message : 'Ошибка подключения';
      errorEl.style.display = 'block';
      btn.disabled = false;
      btn.textContent = originalText || 'ВОЙТИ В ИГРУ';
    }
  });
}
